/** axios 封装的接口请求方法 */
import axios from "axios";
import { ElMessage } from "element-plus";
import { getRuntimeConfig } from "@/core/runtimeConfig";
import { goLogin } from "@/core/navigation";
import {
  clearAuthCookies,
  getAuthCookie,
  parseUserInfoCookie,
  TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  writeTokenCookies,
  writeUserInfoCookie,
} from "@/core/auth/token";
import { remindErrorMessage } from "@/utils/utils";

axios.defaults.withCredentials = true;

let baseUrl = `https://kg.ringdata.com/api/`; //zgx
let baseAuthUrl = `https://i.ringdata.com/uums/`; //175
const OAUTH_BASIC = "Basic bW9uZXR3YXJlOm1vbmV0d2FyZTIwMTkuQHNoLmNvbQ==";

/** 与后端 ErrorCode.CUSTOM_MSG 一致，httpPost 已统一弹窗 */
export const API_CODE_CUSTOM_MSG = 10000;

let sessionExpiredHandling = false;

/** 业务错误是否已在 httpPost / httpAuthPost 中提示，避免页面重复 ElMessage */
export function isApiErrorMessageShown(res) {
  return res?.code === API_CODE_CUSTOM_MSG;
}

/** 非 0 且未在拦截器弹过的错误，由页面补提示 */
export function showApiErrorMessage(res) {
  if (!res?.message || res?.code === 0 || isApiErrorMessageShown(res)) {
    return;
  }
  ElMessage({
    message: res.message,
    type: "warning",
  });
}

function getApiBaseUrl() {
  return getRuntimeConfig().API_BASE_URL;
}

function getAuthBaseUrl() {
  return getRuntimeConfig().AUTH_BASE_URL;
}

function getBearerToken() {
  return getAuthCookie(TOKEN_KEY) || "";
}

function handleSessionExpired(message) {
  if (sessionExpiredHandling) {
    return;
  }
  sessionExpiredHandling = true;
  ElMessage({
    message: message || "登录信息已失效，请重新登录",
    type: "warning",
  });
  clearAuthCookies();
  setTimeout(() => {
    sessionExpiredHandling = false;
    goLogin();
  }, 1000);
}

/**
 * post 请求（业务 API）
 */
const httpPost = function (url = "", data = {}, contentType, rType) {
  const instance = axios.create({
    baseURL: getApiBaseUrl(),
    withCredentials: false,
    headers: {
      Accept: "application/json;charset=UTF-8",
      "Content-type": contentType ? contentType : "application/json",
      Authorization: getBearerToken() ? `Bearer ${getBearerToken()}` : "",
    },
    responseType: rType,
  });
  return instance
    .post(url, data)
    .then((result) => {
      const res = result.data;
      if (res && res.code === 0) {
        return Promise.resolve(res);
      }
      if (res?.code === 15000) {
        return res;
      }
      if (res?.code === API_CODE_CUSTOM_MSG) {
        ElMessage({
          message: res.message,
          type: "warning",
        });
        return res;
      }
      if (res?.code === 10001) {
        handleSessionExpired("登录信息已失效，请重新登录");
        return res;
      }
      if (res?.code === 10010) {
        return Promise.resolve(res);
      }
      return Promise.resolve(res);
    })
    .catch((error) => {
      const res = error.response;
      if (res && (res.status === 400 || res.status === 401)) {
        handleSessionExpired("登录信息已失效，请重新登录");
        return res?.data || res;
      }
      return {
        code: -1,
        message: "网络请求异常，请稍后再试或联系客服！",
      };
    });
};

/**
 * ring-auth OAuth token 请求
 */
const httpPostToken = function (url = "", data) {
  const instance = axios.create({
    baseURL: getAuthBaseUrl(),
    withCredentials: false,
    headers: {
      Authorization: OAUTH_BASIC,
      "content-type": "application/x-www-form-urlencoded;charset=utf-8",
    },
  });
  return instance
    .post(url, data)
    .then((result) => {
      const res = result.data;
      if (result && result.status === 200) {
        if (res != null && res.access_token) {
          writeTokenCookies(res.access_token);
          if (res.userInfo) {
            writeUserInfoCookie(res.userInfo);
          }
          return Promise.resolve({
            code: 0,
            message: "登录成功",
            data: res,
          });
        }
        return {
          code: -1,
          message: "认证失败，请稍后再试或联系客服！",
        };
      }
      return {
        code: -1,
        message: "认证失败，请稍后再试或联系客服！",
      };
    })
    .catch((error) => {
      const res = error.response;
      if (
        res &&
        (res.status === 400 || res.status === 401 || res.status === 500)
      ) {
        const payload = {
          code: res.data?.code ?? -1,
          message: res.data?.message ?? "认证失败",
        };
        ElMessage({
          message: payload.message,
          type: "warning",
        });
        return payload;
      }
      remindErrorMessage("网络请求异常，请稍后再试或联系客服！");
      return {
        code: -1,
        message: "网络请求异常，请稍后再试或联系客服！",
      };
    });
};

/**
 * OAuth refresh_token 刷新
 */
const httpPostRefreshToken = function () {
  const refreshToken = getAuthCookie(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    return Promise.resolve({
      code: -1,
      message: "无刷新令牌",
    });
  }
  return httpPostToken(
    "/oauth/token",
    `grant_type=refresh_token&refresh_token=${encodeURIComponent(refreshToken)}`
  );
};

/**
 * 认证中心 post 请求（退出等）
 */
const httpAuthPost = async function (url = "", data = {}) {
  let token = getBearerToken();
  if (!token) {
    const refreshToken = getAuthCookie(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      const refreshRes = await httpPostRefreshToken();
      if (refreshRes?.code === 0) {
        token = getBearerToken();
      } else {
        handleSessionExpired("登录信息已失效，请重新登录");
        return refreshRes;
      }
    }
  }

  const instance = axios.create({
    baseURL: getAuthBaseUrl(),
    withCredentials: false,
    headers: {
      Accept: "application/json;charset=UTF-8",
      "content-type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    },
  });

  return instance
    .post(url, JSON.stringify(data))
    .then((result) => {
      const res = result.data;
      if (res && res.code === 0) {
        return Promise.resolve(res);
      }
      if (res?.code === API_CODE_CUSTOM_MSG) {
        ElMessage({
          message: res.message,
          type: "warning",
        });
      }
      return Promise.resolve(res);
    })
    .catch((error) => {
      const res = error.response;
      if (
        res &&
        (res.status === 400 || res.status === 401 || res.status === 500)
      ) {
        if (res.data?.message) {
          ElMessage({
            message: res.data.message,
            type: "warning",
          });
        }
        handleSessionExpired("登录信息已失效，请重新登录");
        return res?.data || res;
      }
      return {
        code: -1,
        message: "网络请求异常，请稍后再试或联系客服！",
      };
    });
};

export {
  getApiBaseUrl,
  getAuthBaseUrl,
  httpPost,
  httpPostToken,
  httpPostRefreshToken,
  httpAuthPost,
  parseUserInfoCookie,
};
